import { useEffect, useRef, useState } from 'react';
import { Search, LayoutGrid, Building2, Users, Building, ClipboardCheck, ArrowRightLeft, FileCode2, Network, Bot } from 'lucide-react';
import type { ControlCenterFeature } from '../../models';
import { useFeatureFlags } from '../../features/FeatureFlags';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

const commands: { path: string; label: string; icon: typeof LayoutGrid; hint: string; feature?: ControlCenterFeature }[] = [
  { path: '/', label: 'Перейти к обзору', icon: LayoutGrid, hint: 'Дашборд' },
  { path: '/medical-checkups', label: 'Открыть медосмотры', icon: ClipboardCheck, hint: 'Очередь и ошибки', feature: 'medicalCheckups' },
  { path: '/queue-settings', label: 'Открыть маршрутизацию', icon: ArrowRightLeft, hint: 'Переброс', feature: 'queueSettings' },
  { path: '/document-templates', label: 'Открыть шаблоны', icon: FileCode2, hint: 'Документы', feature: 'documentTemplates' },
  { path: '/tenants', label: 'Открыть организации', icon: Building2, hint: 'Тенанты', feature: 'tenants' },
  { path: '/meshcentral', label: 'Открыть MeshCentral', icon: Network, hint: 'Парк терминалов', feature: 'meshcentral' },
  { path: '/ai-assistant', label: 'Открыть ИИ-помощника', icon: Bot, hint: 'Диагностика' },
  { path: '/users', label: 'Открыть пользователей', icon: Users, hint: 'Управление', feature: 'users' },
  { path: '/companies', label: 'Открыть клиентов ЛК', icon: Building, hint: 'Справочник', feature: 'companies' },
];

export function CommandPalette({ open, onClose, onNavigate }: CommandPaletteProps) {
  const { isEnabled } = useFeatureFlags();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!open) return null;

  const filtered = commands.filter((command) => (!command.feature || isEnabled(command.feature)) &&
    command.label.toLowerCase().includes(query.toLowerCase()));

  const selectFirst = () => {
    if (filtered[0]) { onNavigate(filtered[0].path); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl overflow-hidden rounded-xl border border-border bg-popover shadow-2xl">
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && selectFirst()}
            placeholder="Найти раздел..."
            className="w-full bg-transparent py-4 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">ESC</kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto p-2">
          {filtered.length > 0 && (
            <>
              <p className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Навигация</p>
              {filtered.map((c) => (
                <button
                  key={c.path}
                  onClick={() => { onNavigate(c.path); onClose(); }}
                  className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <c.icon className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-left">{c.label}</span>
                  <span className="text-xs text-muted-foreground">{c.hint}</span>
                </button>
              ))}
            </>
          )}

          {filtered.length === 0 && <p className="px-3 py-8 text-center text-sm text-muted-foreground">Раздел не найден</p>}
        </div>
      </div>
    </div>
  );
}
